import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PasswordInput } from './PasswordInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, UserPlus, Trash2, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { adminApi } from '../utils/api';

export const AdminUsersDialog = ({ open, onOpenChange }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listUsers();
      setUsers(res.data.users);
    } catch {
      toast({ title: 'Xəta', description: 'İstifadəçilər yüklənə bilmədi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (open) loadUsers();
  }, [open, loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      toast({ title: 'Xəta', description: 'Parol ən azı 8 simvol olmalı, hərf və rəqəm ehtiva etməlidir', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.createUser(name, email, password);
      toast({ title: 'Uğurlu', description: `${email} üçün admin hesabı yaradıldı` });
      setName(''); setEmail(''); setPassword(''); setShowForm(false);
      loadUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Admin yaradıla bilmədi';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => d.msg || '').filter(Boolean).join('; ') || msg;
      toast({ title: 'Xəta', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userEmail) => {
    if (!window.confirm(`${userEmail} hesabını silmək istədiyinizə əminsiniz?`)) return;
    try {
      await adminApi.deleteUser(userEmail);
      toast({ title: 'Silindi', description: `${userEmail} hesabı silindi` });
      loadUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({ title: 'Xəta', description: typeof detail === 'string' ? detail : 'Silinə bilmədi', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-700" />
            Admin istifadəçilər
          </DialogTitle>
          <DialogDescription>
            Əməkdaşlarınıza admin panelə giriş hüququ verin və ya silin.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {users.map((user) => (
              <div key={user.email} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-2.5" data-testid={`admin-user-${user.email}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {user.name || '—'}
                    {user.isSelf && <span className="ml-2 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">Siz</span>}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                {!user.isSelf && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(user.email)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-admin-${user.email}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <form onSubmit={handleCreate} className="space-y-3 border-t pt-4">
            <div>
              <Label htmlFor="new-admin-name">Ad Soyad</Label>
              <Input id="new-admin-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" data-testid="new-admin-name-input" />
            </div>
            <div>
              <Label htmlFor="new-admin-email">Elektron ünvan</Label>
              <Input id="new-admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" className="mt-1" data-testid="new-admin-email-input" />
            </div>
            <div>
              <Label htmlFor="new-admin-password">Parol</Label>
              <div className="mt-1">
                <PasswordInput id="new-admin-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" data-testid="new-admin-password-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white" data-testid="create-admin-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Yarat
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Ləğv et</Button>
            </div>
          </form>
        ) : (
          <Button onClick={() => setShowForm(true)} variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="show-add-admin-form-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Yeni admin əlavə et
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
